/**
 * EntitySearchField — REFERENCE field type renderer (H4: Cross-subgraph entity lookup)
 *
 * Renders a searchable autocomplete for entity references. Used by EntityFormGenerator
 * when field.fieldType === 'REFERENCE'. Queries the backend `entitySearch` resolver
 * which handles constellation/tenant scoping automatically.
 *
 * FERP Principles:
 * - Dumb Frontend: scope inference lives in backend (entity_search.rs)
 * - Repository pattern: all data via GraphQL entitySearch query
 * - Domain-agnostic: works for Actor, FhirPatient, FhirMedication, etc.
 * - DRR-055: field configuration comes from backend FormSchema REFERENCE config
 */

import { useState, useCallback, useRef } from 'react';
import { Autocomplete, Loader, Text } from '@mantine/core';
import { useDebouncedValue } from '@mantine/hooks';
import { useQuery } from '@apollo/client/react';
import { gql } from '@apollo/client';

// ── GraphQL Operation (inline until codegen) ─────────────────────────────────

const ENTITY_SEARCH = gql`
  query EntitySearch($input: EntitySearchInput!) {
    entitySearch(input: $input) {
      id
      displayName
      secondary
      subgraph
      entityType
    }
  }
`;

// ── Types ────────────────────────────────────────────────────────────────────

interface ReferenceConfig {
  subgraph: string;
  entityType: string;
  query?: string | null;
  valuePath?: string | null;
  labelPath?: string | null;
  searchFields?: string[] | null;
  allowCreate?: boolean | null;
}

interface FormField {
  name: string;
  label: string;
  fieldType: string;
  required: boolean;
  placeholder?: string | null;
  helpText?: string | null;
  referenceConfig?: ReferenceConfig | null;
}

interface EntitySearchResult {
  id: string;
  displayName: string;
  secondary?: string | null;
  subgraph: string;
  entityType: string;
}

interface EntitySearchFieldProps {
  field: FormField;
  value: unknown;
  onChange: (value: unknown) => void;
  disabled?: boolean;
  error?: string;
}

// ── Component ────────────────────────────────────────────────────────────────

export function EntitySearchField({
  field,
  value,
  onChange,
  disabled,
  error,
}: EntitySearchFieldProps) {
  const ref = field.referenceConfig;
  const [searchText, setSearchText] = useState(String(value ?? ''));
  const [debouncedSearch] = useDebouncedValue(searchText, 300);
  const resultsMapRef = useRef<Map<string, EntitySearchResult>>(new Map());

  const { data, loading } = useQuery<{
    entitySearch: EntitySearchResult[];
  }>(ENTITY_SEARCH, {
    variables: {
      input: {
        subgraph: ref?.subgraph ?? 'core',
        entityType: ref?.entityType ?? 'Actor',
        query: debouncedSearch,
        limit: 20,
      },
    },
    skip: !debouncedSearch || debouncedSearch.length < 2,
  });

  const results = data?.entitySearch ?? [];

  // Update results map for value lookup on selection
  results.forEach((r) => {
    resultsMapRef.current.set(r.displayName, r);
  });

  const autocompleteData = results.map((r) => ({
    value: r.displayName,
    label: r.secondary ? `${r.displayName} (${r.secondary})` : r.displayName,
  }));

  const handleChange = useCallback(
    (val: string) => {
      setSearchText(val);
      // If user selected a result, emit the ID
      const match = resultsMapRef.current.get(val);
      if (match) {
        onChange(match.id);
      } else {
        // User is still typing — emit the text as-is
        onChange(val);
      }
    },
    [onChange],
  );

  return (
    <Autocomplete
      label={field.label}
      placeholder={field.placeholder ?? `Search ${ref?.entityType ?? 'entity'}...`}
      description={field.helpText}
      value={searchText}
      onChange={handleChange}
      data={autocompleteData}
      required={field.required}
      disabled={disabled}
      error={error}
      rightSection={loading ? <Loader size={14} /> : undefined}
      filter={({ options }) => options}
      limit={20}
    />
  );
}
